import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarCheck, CalendarDays, Clock, Download, Loader2, MapPin, Shield, Video } from 'lucide-react';
import SiteLogo from '../../components/Layout/SiteLogo';
import LanguageSwitcher from '../../components/Layout/LanguageSwitcher';
import SlotPicker, { Slot, browserTimezone } from './SlotPicker';

type LocaleCode = 'pt' | 'en' | 'es';

interface LocaleBlock {
  publicTitle?: string;
  description?: string;
}

export interface PublicLinkInfo {
  i18n?: Partial<Record<LocaleCode, LocaleBlock>>;
  publicTitle?: string;
  description?: string;
  durationMin: number;
  timezone: string;
  meetingUrl: boolean;
  location?: string;
  expiresAt: string;
  lang?: LocaleCode;
  host?: { photoUrl: string; displayName: Partial<Record<LocaleCode, string>> } | null;
}

interface Confirmation {
  startAt: string;
  endAt: string;
  meetingUrl?: string;
  googleCalUrl?: string;
  manageToken?: string;
}

const pageShell = 'min-h-[100dvh] bg-slate-100';
const inputClass =
  'mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const icsHref = (manageToken: string) => `${axios.defaults.baseURL}/agendamento/public/reserva/${manageToken}/ics`;

export const pickText = (info: PublicLinkInfo, lang: LocaleCode, field: keyof LocaleBlock) =>
  info.i18n?.[lang]?.[field]?.trim() || info.i18n?.pt?.[field]?.trim() || info[field] || '';

export const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export function formatSlotRange(startAt: string, endAt: string, lang: string) {
  const day = new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(startAt)
  );
  const time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' });
  return `${time.format(new Date(startAt))} – ${time.format(new Date(endAt))}, ${day}`;
}

// Painel lateral estilo Calendly: anfitrião, título, duração, formato e descrição
export const MeetingSummary: React.FC<{ info: PublicLinkInfo; slot?: Slot | null; title?: string }> = ({ info, slot, title }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split('-')[0] || 'pt') as LocaleCode;
  const hostName = info.host ? info.host.displayName[lang] || info.host.displayName.pt || '' : '';
  const description = pickText(info, lang, 'description');

  return (
    <div>
      {info.host && (
        <div className="flex items-center gap-3 mb-4">
          {info.host.photoUrl ? (
            <img src={info.host.photoUrl} alt={hostName} className="h-12 w-12 rounded-full object-cover" />
          ) : null}
          {hostName && <p className="text-sm font-medium text-gray-600">{t('agendamento.hostedBy', { nome: hostName })}</p>}
        </div>
      )}
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">{title || pickText(info, lang, 'publicTitle')}</h1>
      <ul className="mt-4 space-y-2.5 text-sm text-gray-600">
        <li className="flex items-center gap-2.5">
          <Clock className="w-4 h-4 shrink-0 text-gray-400" />
          {t('agendamento.duration', { min: info.durationMin })}
        </li>
        {info.meetingUrl && (
          <li className="flex items-center gap-2.5">
            <Video className="w-4 h-4 shrink-0 text-gray-400" />
            {t('agendamento.videoCall')}
          </li>
        )}
        {info.location && (
          <li className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 shrink-0 text-gray-400 mt-0.5" />
            <span>{info.location}</span>
          </li>
        )}
        {slot && (
          <li className="flex items-start gap-2.5 text-primary-700 font-medium">
            <CalendarDays className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="first-letter:uppercase">{formatSlotRange(slot.startAt, slot.endAt, i18n.language)}</span>
          </li>
        )}
      </ul>
      {description && <p className="mt-4 text-sm text-gray-700 whitespace-pre-line leading-relaxed">{description}</p>}
    </div>
  );
};

export const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}>
    <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
      <header className="flex flex-col items-center gap-3 mb-5 sm:mb-8 lg:flex-row lg:justify-between">
        <SiteLogo size="lg" nameClassName="font-bold text-gray-900 text-xl sm:text-2xl" />
        <LanguageSwitcher compact variant="floating" className="shadow-md" />
      </header>
      {children}
    </div>
  </div>
);

export const StatusCard: React.FC<{ icon: React.ReactNode; title: string; children?: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
    <div className="max-w-md w-full text-center bg-white rounded-xl shadow-lg p-8">
      <div className="flex justify-center mb-4">{icon}</div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
      {children}
    </div>
  </div>
);

const AgendamentoPublic: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<PublicLinkInfo | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [step, setStep] = useState<'pick' | 'details'>('pick');
  const [slot, setSlot] = useState<Slot | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', whatsapp: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState<Confirmation | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/agendamento/public/${token}`);
        setInfo(res.data);
        // Idioma do link só vale se o visitante ainda não escolheu um idioma no site
        let userChoseLang = false;
        try {
          userChoseLang = !!localStorage.getItem('neia_lang');
        } catch {
          /* storage indisponível */
        }
        if (res.data.lang && !userChoseLang) i18n.changeLanguage(res.data.lang);
      } catch (err) {
        setErrorMsg(errorMessage(err, t('agendamento.linkExpired')));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const lang = (i18n.language?.split('-')[0] || 'pt') as LocaleCode;
  const title = useMemo(() => (info ? pickText(info, lang, 'publicTitle') : ''), [info, lang]);

  const handleSelect = (s: Slot) => {
    setSlot(s);
    setSubmitError('');
    setStep('details');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (!form.firstName.trim() || !form.lastName.trim() || !EMAIL_RE.test(form.email.trim())) {
      setSubmitError(t('agendamento.requiredFields'));
      return;
    }
    if (!consent) {
      setSubmitError(t('agendamento.consentRequired'));
      return;
    }
    if (!slot) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`/agendamento/public/${token}/reservar`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        startAt: slot.startAt,
        lang,
        clientTimezone: browserTimezone(),
        consentAccepted: true
      });
      setDone(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'SLOT_TAKEN') {
        // Volta ao calendário recarregado para escolher outro horário
        setSlot(null);
        setStep('pick');
        setPickerKey((k) => k + 1);
      }
      setSubmitError(errorMessage(err, t('agendamento.submitError')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading') as string} />
      </div>
    );
  }

  if (!info) {
    return (
      <StatusCard icon={<Shield className="w-12 h-12 text-amber-500" />} title={t('agendamento.linkUnavailable')}>
        <p className="text-gray-600">{errorMsg}</p>
      </StatusCard>
    );
  }

  if (done) {
    return (
      <PublicShell>
        <div className="mx-auto max-w-xl bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
          <CalendarCheck className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">{t('agendamento.confirmedTitle')}</h1>
          <p className="mt-2 text-gray-600">{t('agendamento.confirmedMsg', { email: form.email.trim() })}</p>
          <div className="mt-6 rounded-xl border border-gray-200 p-4 text-left">
            <p className="font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-sm text-primary-700 font-medium first-letter:uppercase">
              {formatSlotRange(done.startAt, done.endAt, i18n.language)}
            </p>
            {done.meetingUrl && (
              <p className="mt-2 text-sm text-gray-600 break-all">
                {t('agendamento.meetingLink')}:{' '}
                <a href={done.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                  {done.meetingUrl}
                </a>
              </p>
            )}
          </div>
          <div className="mt-6 grid gap-2.5">
            {done.meetingUrl && (
              <a
                href={done.meetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap bg-primary-600 text-white font-medium hover:bg-primary-700"
              >
                <Video className="w-4 h-4 shrink-0" />
                {t('agendamento.joinMeeting')}
              </a>
            )}
            {done.googleCalUrl && (
              <a
                href={done.googleCalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap border border-primary-600 text-primary-700 font-medium hover:bg-primary-50"
              >
                <CalendarDays className="w-4 h-4 shrink-0" />
                {t('agendamento.addGoogle')}
              </a>
            )}
            {done.manageToken && (
              <a
                href={icsHref(done.manageToken)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                <Download className="w-4 h-4 shrink-0" />
                {t('agendamento.downloadIcs')}
              </a>
            )}
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden lg:grid lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="p-5 sm:p-6 border-b border-gray-100 lg:border-b-0 lg:border-r">
          <MeetingSummary info={info} slot={step === 'details' ? slot : null} title={title} />
          {info.expiresAt && (
            <p className="mt-5 text-xs text-gray-400">
              {t('agendamento.expiresUntil', { data: new Date(info.expiresAt).toLocaleDateString(i18n.language) })}
            </p>
          )}
        </aside>

        <section className="p-5 sm:p-6 min-w-0">
          {step === 'pick' ? (
            <>
              {submitError && <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{submitError}</p>}
              <SlotPicker key={pickerKey} token={token!} expiresAt={info.expiresAt} selected={slot} onSelect={handleSelect} />
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="max-w-lg">
              <button
                type="button"
                onClick={() => setStep('pick')}
                className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('agendamento.back')}
              </button>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('agendamento.stepDetails')}</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm text-gray-700">{t('agendamento.firstName')} *</span>
                  <input
                    type="text"
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    placeholder={t('agendamento.firstNamePh') as string}
                    className={inputClass}
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-700">{t('agendamento.lastName')} *</span>
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    placeholder={t('agendamento.lastNamePh') as string}
                    className={inputClass}
                    required
                  />
                </label>
              </div>
              <label className="block mt-4">
                <span className="text-sm text-gray-700">{t('agendamento.email')} *</span>
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t('agendamento.emailPh') as string}
                  className={inputClass}
                  required
                />
              </label>
              <label className="block mt-4">
                <span className="text-sm text-gray-700">
                  {t('agendamento.whatsapp')} <span className="text-gray-400">({t('agendamento.optional')})</span>
                </span>
                <input
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder={t('agendamento.whatsappPh') as string}
                  className={inputClass}
                />
              </label>

              <label className="flex items-start gap-2 mt-5 text-sm text-gray-700">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
                <span>{t('agendamento.consent')}</span>
              </label>

              {submitError && <p className="text-sm text-red-600 mt-3">{submitError}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('agendamento.submit')}
              </button>
            </form>
          )}
        </section>
      </div>
    </PublicShell>
  );
};

export default AgendamentoPublic;
